import { useQuery, useZero } from "@rocicorp/zero/react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useCurrentUserGrants } from "../hooks/useCurrentUserGrants";
import type { Connector } from "../lib/connector";
import { getWritableScopeIds } from "../lib/getWritableScopeIds";
import { ConnectorsBar } from "./ConnectorsBar";

export function WorkflowChatConnectorsBar({
  workflowId,
  branchId,
  spaceId,
  canWrite,
  disabled,
  availableConnectors,
}: {
  workflowId: string;
  branchId: string;
  spaceId: string;
  canWrite: boolean;
  disabled?: boolean;
  availableConnectors: readonly Connector[];
}) {
  const z = useZero();
  const [grants, grantsResult] = useCurrentUserGrants();
  const [approvals, approvalsResult] = useQuery(
    queries.workflowChatConnectorApprovals({ workflowId })
  );
  const [branches, branchesResult] = useQuery(
    queries.workflowStepConnectors({ workflowId })
  );
  const stepConnectorIds = new Set(
    branches.flatMap((branch) =>
      (branch.headCommit?.appUsages ?? []).map((usage) => usage.connectedAppId)
    )
  );
  const connectedIds = new Set([
    ...approvals.map((usage) => usage.connectedAppId),
    ...stepConnectorIds,
  ]);
  const availableIds = getWritableScopeIds(
    grants,
    availableConnectors.map((app) => app.id),
    "connected_app:use"
  );
  const connected = availableConnectors
    .filter((app) => connectedIds.has(app.id))
    .sort(
      (a, b) =>
        Number(stepConnectorIds.has(a.id)) - Number(stepConnectorIds.has(b.id))
    )
    .map((app) => ({
      ...app,
      section: stepConnectorIds.has(app.id)
        ? "Used by workflow steps"
        : "Attached to chat",
      removable: !stepConnectorIds.has(app.id),
    }));

  return (
    <ConnectorsBar
      parentNoun="workflow chat"
      canWrite={canWrite}
      disabled={
        disabled ||
        [grantsResult, approvalsResult, branchesResult].some(
          (result) => result.type !== "complete"
        )
      }
      spaceId={spaceId}
      connected={connected}
      excludeIds={connectedIds}
      availableIds={availableIds}
      associate={(connectedAppId) =>
        z.mutate(
          sharedMutators.associateBranchChatWithConnectedApp({
            branchId,
            connectedAppId,
          })
        )
      }
      remove={(connectedAppId) =>
        z.mutate(
          sharedMutators.dissociateBranchChatFromConnectedApp({
            branchId,
            connectedAppId,
          })
        )
      }
      menuMargin={6}
      subtleAddButton
      avatarSize={32}
    />
  );
}
